import { useMemo } from 'react';

import type {
  SubscriptionPlanType,
  SubscriptionPrices,
} from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';

import { useUserStore, userStoreSelector } from '../../../../stores/userStore';
import { MonetaryValue } from '../../../../types/Pricing';
import {
  formatMonetaryValue,
  getYearlyCharge,
} from '../../../../utils/billing';
import { getPlanPrice } from '../../utils';

interface UsePlanIntervalDataParams {
  plan: SubscriptionPlanType;
}

interface PlanIntervalLabels {
  price: string;
  priceSubLabel: string;
}

interface PlanIntervalData {
  monthly: PlanIntervalLabels;
  yearly: PlanIntervalLabels;
  yearlySave: string;
}

const getPrice = (options: {
  plan: SubscriptionPlanType;
  isYearly: boolean;
  perMonth: boolean;
  subscriptionPrices: SubscriptionPrices;
  country: string | null;
}): MonetaryValue => {
  const { plan, isYearly, perMonth, subscriptionPrices, country } = options;

  const price = getPlanPrice({
    plan,
    isYearly,
    subscriptionPrices,
    country,
  });

  if (!isYearly || perMonth) {
    return price;
  }

  return getYearlyCharge(price);
};

const getPlanIntervalLabels = (options: {
  isYearly: boolean;
  isTeam: boolean;
  plan: SubscriptionPlanType;
  subscriptionPrices: SubscriptionPrices;
  country: string | null;
}): PlanIntervalLabels => {
  const { isYearly, isTeam, plan, subscriptionPrices, country } = options;

  const pricePerMonth = getPrice({
    plan,
    isYearly,
    perMonth: true,
    subscriptionPrices,
    country,
  });

  if (isTeam) {
    return {
      price: formatMonetaryValue(pricePerMonth, { fractionDigits: 0 }),
      priceSubLabel: '/ month (per user)',
    };
  }
  if (!isYearly) {
    return {
      price: formatMonetaryValue(pricePerMonth, { fractionDigits: 0 }),
      priceSubLabel: '/ month',
    };
  }

  const price = getPrice({
    plan,
    isYearly,
    perMonth: false,
    subscriptionPrices,
    country,
  });

  return {
    price: formatMonetaryValue(pricePerMonth, { fractionDigits: 0 }),
    priceSubLabel: `(${formatMonetaryValue(price, {
      fractionDigits: country === 'ID' ? 2 : 0, // we do not want the price for Indonesia to be rounded off
    })} billed annually)`,
  };
};

/**
 * get savings in percent for using the yearly plan
 */
const getYearlySave = (
  plan: SubscriptionPlanType,
  subscriptionPrices: SubscriptionPrices,
  country: string | null
): string => {
  const priceMonthly = getPrice({
    plan,
    isYearly: false,
    perMonth: true,
    subscriptionPrices,
    country,
  });
  const priceYearly = getPrice({
    plan,
    isYearly: true,
    perMonth: true,
    subscriptionPrices,
    country,
  });

  if (!priceMonthly || !priceYearly) {
    return formatMonetaryValue(
      { amount: 0, currency: priceMonthly.currency },
      { fractionDigits: 0 }
    );
  }

  return formatMonetaryValue(
    {
      amount: (priceMonthly.amount - priceYearly.amount) * 12,
      currency: priceMonthly.currency,
    },
    { fractionDigits: 0 }
  );
};

const usePlanIntervalData = ({
  plan,
}: UsePlanIntervalDataParams): PlanIntervalData => {
  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const subscriptionPrices = useSubscriptionStore(
    subscriptionStoreSelector.subscriptionPrices
  );

  return useMemo(
    () => ({
      monthly: getPlanIntervalLabels({
        isYearly: false,
        isTeam,
        plan,
        subscriptionPrices,
        country,
      }),
      yearly: getPlanIntervalLabels({
        isYearly: true,
        isTeam,
        plan,
        subscriptionPrices,
        country,
      }),
      yearlySave: getYearlySave(plan, subscriptionPrices, country),
    }),
    [isTeam, plan, subscriptionPrices, country]
  );
};

export default usePlanIntervalData;
